// api/problemDetails.ts
//
// The InternalApi answers a failed command with RFC 9457 ProblemDetails. The generated client
// declares that body on 400, 404 and 409 only; on those Kiota throws the parsed problem as a plain
// object, not an Error, and on any other status a bare DefaultApiError without the body. Both carry
// the status code. This module keeps the raw body: the fetch the client uses reads the problem off
// every failed response and remembers the latest one, and describeApiError pairs a thrown error
// with that problem by status code. Commands run one at a time from a drawer, so the latest failed
// response is the one the caught error belongs to. The raw body rather than Kiota's parse: Kiota
// files the validation failures under `additionalData`.
import type { ApiError } from '@microsoft/kiota-abstractions';

export interface ProblemDetails {
    type?: string;
    title?: string;
    status?: number;
    detail?: string;
    //FluentValidation failures, keyed by property
    errors?: Record<string, string[]>;
}

export interface ApiErrorDescription {
    status?: number;
    problem?: ProblemDetails;
    //what to show the user: the problem's detail, its validation messages, or its title
    message: string;
}

let lastProblem: { status: number; problem: ProblemDetails } | undefined;

export async function problemCapturingFetch(url: string, init: RequestInit): Promise<Response> {
    const response = await fetch(url, init);
    if (!response.ok) {
        lastProblem = { status: response.status, problem: await readProblem(response) };
    }
    return response;
}

async function readProblem(response: Response): Promise<ProblemDetails> {
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('json')) return {};
    try {
        return (await response.clone().json()) as ProblemDetails;
    } catch {
        return {};
    }
}

//a declared status throws a plain object, so no instanceof Error here
function isApiError(error: unknown): error is ApiError {
    return typeof error === 'object' && error !== null && 'responseStatusCode' in error;
}

export function describeApiError(error: unknown): ApiErrorDescription {
    const status = isApiError(error) ? error.responseStatusCode : undefined;
    const problem = status !== undefined && lastProblem?.status === status ? lastProblem.problem : undefined;

    const validationMessages = Object.values(problem?.errors ?? {}).flat();
    const message = problem?.detail || validationMessages.join(' ') || problem?.title || (status ? `The request failed with status ${status}.` : error instanceof Error ? error.message : 'The request failed.');

    return { status, problem, message };
}

//400, 404 and 409 are the outcome of the command the user just issued: shown inside the drawer,
//which stays open with the input intact. Everything else is an error toast.
export function isCommandOutcome(status: number | undefined): boolean {
    return status === 400 || status === 404 || status === 409;
}
